// Lớp phủ toàn cục bằng search param (FSD.md §2.2): ?customer=<id>, ?deal=<id>, ?task=new&taskCustomer=<id>…
// mở được từ mọi trang, chia sẻ đường dẫn được; mở bằng push (Quay lại đóng lớp phủ), đóng bằng replace — trừ khi mục
// lịch sử hiện tại chính là mục open() vừa đẩy: khi đó đóng = quay lại (không để lại bản sao trang trong lịch sử).
// Sổ OverlayDef do app/router gộp từ pages/<m>/meta.ts; sổ tham số đi kèm (OverlayRegistry) dựng tĩnh ở đó và cấp qua
// OverlayRegistryProvider cho useOverlay().
import { createContext, createElement, useCallback, useContext, useMemo, type ComponentType, type ReactNode } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';

export interface OverlayDef {
  /** Khoá search param (?key=value). */
  key: string;
  /** Module phải thuộc visibleModules thì lớp phủ mới mở. */
  module: string;
  /**
   * Quyền cần có (một trong các khoá) — OverlayHost kiểm chung, ngoài module hiển thị. Thiếu ⇒ thay component bằng hộp
   * "chưa có quyền" (`denied`): đường dẫn chia sẻ / gõ tay của người không đủ quyền không mở form.
   */
  perm?: string | readonly string[];
  /** Hộp báo khi thiếu `perm`: tiêu đề (theo giá trị lớp phủ) và câu báo. */
  denied?: { title: string | ((value: string) => string); text: string };
  /** Tham số đi kèm, xoá cùng khi đóng và khi mở lại khoá này (VD forCustomer, taskCustomer). */
  companions?: string[];
  layer: 'drawer' | 'modal';
  component: ComponentType<{ value: string; close: () => void }>;
}

type Raw = Record<string, unknown>;

/** Giá trị lớp phủ trong URL (router có thể đã đổi '5' thành số) ⇒ chuỗi; rỗng ⇒ null. */
export function overlayValue(search: Raw, key: string): string | null {
  const v = search[key];
  if (v === undefined || v === null || v === '' || v === false) return null;
  return String(v);
}

/** Search sau khi đóng: bỏ khoá và mọi tham số đi kèm. */
export function withoutOverlay(s: Raw, keys: readonly string[]): Raw {
  const out: Raw = { ...s };
  for (const k of keys) delete out[k];
  return out;
}

/**
 * Search sau khi mở lớp phủ `key=value`: bỏ tham số đi kèm cũ của chính khoá này (mở căn khác không mang theo
 * forCustomer của lần trước) rồi áp `extra`.
 */
export const withOverlay = (s: Raw, key: string, value: string, extra?: Raw, companions: readonly string[] = []): Raw => ({
  ...withoutOverlay(s, [key, ...companions]),
  [key]: value,
  ...(extra ?? {}),
});

/** Khoá quyền của lớp phủ (rỗng ⇒ không đòi quyền riêng). */
export const overlayPerms = (d: Pick<OverlayDef, 'perm'>): readonly string[] => (d.perm == null ? [] : typeof d.perm === 'string' ? [d.perm] : d.perm);

/**
 * Lớp phủ đang mở theo URL, chỉ module hiển thị; drawer trước, modal sau (modal vẽ trên drawer). `any` (có một trong các
 * quyền) ⇒ đánh dấu `denied` lớp phủ thiếu `perm`; vắng ⇒ không xét quyền.
 */
export function activeOverlays(search: Raw, defs: readonly OverlayDef[], visible: (moduleId: string) => boolean, any?: (...perms: string[]) => boolean) {
  const on = defs.flatMap((d) => {
    const value = overlayValue(search, d.key);
    if (value == null || !visible(d.module)) return [];
    const perms = overlayPerms(d);
    return [{ def: d, value, denied: !!any && perms.length > 0 && !any(...perms) }];
  });
  return [...on.filter((o) => o.def.layer === 'drawer'), ...on.filter((o) => o.def.layer === 'modal')];
}

// ---------- Sổ tham số đi kèm ----------
/** Khoá lớp phủ ⇒ tham số đi kèm. */
export type OverlayRegistry = ReadonlyMap<string, readonly string[]>;

/** Dựng sổ từ mọi OverlayDef (gọi một lần ở app/router). Khoá trùng giữa hai module ⇒ lỗi (dev), ghi console (prod). */
export function overlayRegistry(defs: readonly OverlayDef[]): OverlayRegistry {
  const out = new Map<string, readonly string[]>();
  const owner = new Map<string, string>();
  for (const d of defs) {
    if (out.has(d.key)) {
      const msg = `Khoá lớp phủ "${d.key}" khai báo hai lần (module ${owner.get(d.key)} và ${d.module})`;
      if (import.meta.env.DEV) throw new Error(msg);
      console.error(msg);
      continue;
    }
    out.set(d.key, d.companions ?? []);
    owner.set(d.key, d.module);
  }
  return out;
}

const EMPTY: OverlayRegistry = new Map();
const RegistryContext = createContext<OverlayRegistry>(EMPTY);
/** Cấp sổ tham số đi kèm cho mọi useOverlay() bên trong (khung ứng dụng + OverlayHost). */
export const OverlayRegistryProvider = ({ registry, children }: { registry: OverlayRegistry; children: ReactNode }) =>
  createElement(RegistryContext.Provider, { value: registry }, children);

export const overlayKeys = (registry: OverlayRegistry, key: string) => [key, ...(registry.get(key) ?? [])];

// ---------- Mở / đóng ----------
/** Dấu trên mục lịch sử do open() đẩy: khoá + search ngay trước khi mở. */
const MARK = '__overlay';
interface Mark {
  key: string;
  from: string;
}
/** Định danh ổn định của một search (không phụ thuộc thứ tự khoá). */
export const searchId = (s: Raw) => JSON.stringify(Object.keys(s).sort().map((k) => [k, s[k]]));

/**
 * Đóng bằng "quay lại" khi mục hiện tại do open(key) đẩy và quay lại cho đúng search sau khi đóng; ngược lại (mở từ
 * đường dẫn chia sẻ, đã đổi tab/bộ lọc trong lúc mở…) ⇒ replace.
 */
export function closeByBack(state: unknown, key: string, next: Raw): boolean {
  const mark = (state as Record<string, unknown> | null | undefined)?.[MARK] as Mark | undefined;
  return !!mark && mark.key === key && mark.from === searchId(next);
}

/** Mở/đóng lớp phủ tại trang hiện tại. */
export function useOverlay() {
  const navigate = useNavigate();
  const router = useRouter();
  const registry = useContext(RegistryContext);
  const open = useCallback(
    (key: string, value: string, extra?: Raw) => {
      const mark: Mark = { key, from: searchId(router.state.location.search as Raw) };
      void navigate({ to: '.', search: ((s: Raw) => withOverlay(s, key, value, extra, registry.get(key))) as never, state: { [MARK]: mark } as never });
    },
    [navigate, router, registry],
  );
  /** `companions` vắng ⇒ lấy theo sổ. */
  const close = useCallback(
    (key: string, companions?: readonly string[]) => {
      const loc = router.state.location;
      const next = withoutOverlay(loc.search as Raw, [key, ...(companions ?? registry.get(key) ?? [])]);
      if (closeByBack(loc.state, key, next)) return router.history.back();
      void navigate({ to: '.', search: (() => next) as never, replace: true });
    },
    [navigate, router, registry],
  );
  return useMemo(() => ({ open, close }), [open, close]);
}
